import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle, Ban, CalendarPlus, CheckCircle2, Eye, HardHat, Info, Loader2, Plus,
    RefreshCw, Star, UserMinus, X,
} from 'lucide-react';
import { Button } from '../Button';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { READ_ONLY_AUSWEG, READ_ONLY_HINWEIS, readOnlyMessage, readOnlyTitle } from '../../services/readOnlyNotice';
import {
    ASSIGNMENT_STATUS_KLASSEN,
    ASSIGNMENT_STATUS_LABELS,
    FreelancerProjectAccess,
    InspectionAssignment,
    canAdminCancel,
    entzugsFolgen,
    formatZeitraum,
    sortiereAssignments,
} from '../../services/assignmentLogic';
import {
    cancelAssignment,
    fetchAssignments,
    fetchProjectAccess,
    grantProjectAccess,
    revokeProjectAccess,
} from '../../services/assignmentService';
import {
    Freelancer,
    FreelancerMatch,
    FreelancerQualification,
    formatAnschrift,
    formatCents,
    formatDistanz,
    heuteIso,
    suggestFreelancers,
} from '../../services/freelancerLogic';
import { fetchAllQualifications, fetchFreelancers } from '../../services/freelancerService';
import { AssignmentDialog } from './AssignmentDialog';

// Projekt-Freischaltung und Begehungsauftraege EINES Projekts
// (ERP/CRM-Plan, Abschnitt 7 — Workflow Schritte 1 und 2, Phase D2).
//
// Duenner Container (Standing Decision): jede fachliche Aussage — wer kaeme in
// Frage und warum nicht, welcher Auftrag laesst sich stornieren, was passiert
// beim Entzug — kommt fertig aus freelancerLogic.ts bzw. assignmentLogic.ts,
// jeder Schreibvorgang aus assignmentService.ts (und dort ausschliesslich ueber
// eine RPC). Hier steht nur Lade- und Dialogzustand.
//
// Innendienst-Sicht (Admin oder Recht „Freelancer verwalten", ab Nr. 153 auch
// in den RPCs). Der Freelancer sieht sein Angebot erst in D3 (Portal) und
// entscheidet dort ueber dieselbe RPC `set_assignment_status`.
//
// Die Vorschlagsliste folgt dem Muster des FreelancerMatchPanel (D1): sie
// zeigt AUCH die Ausgeschlossenen mit Begruendung, weil „warum steht Herr M.
// nicht dabei?" die erste Frage ist, die hier gestellt wird.

interface Props {
    projectId: string;
    projectName?: string;
    /** Projektkoordinaten fuer die Entfernungssortierung. Ohne sie bleibt die
     *  Vorschlagsliste leer und nennt den Grund. */
    lat?: number | null;
    lng?: number | null;
    /** Lesemodus des Projekts (aus „Offene Positionen" geoeffnet). Die
     *  Freischaltungen und Auftraege bleiben LESBAR — nur die vier
     *  schreibenden Wege (freischalten, Auftrag erteilen, entziehen,
     *  stornieren) sind gesperrt. Sie laufen ueber RPCs und wuerden dauerhaft
     *  wirken, waehrend der Nutzer glaubt, nur zu schauen. */
    isReadOnly?: boolean;
}

const StatusAbzeichen: React.FC<{ status: InspectionAssignment['status'] }> = ({ status }) => (
    <span
        className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-bold ${ASSIGNMENT_STATUS_KLASSEN[status]}`}
    >
        {ASSIGNMENT_STATUS_LABELS[status]}
    </span>
);

export const ProjectAccessPanel: React.FC<Props> = ({ projectId, projectName, lat, lng, isReadOnly = false }) => {
    const [freelancers, setFreelancers] = useState<Freelancer[]>([]);
    const [qualis, setQualis] = useState<FreelancerQualification[]>([]);
    const [zugaenge, setZugaenge] = useState<FreelancerProjectAccess[]>([]);
    const [auftraege, setAuftraege] = useState<InspectionAssignment[]>([]);

    const [laedt, setLaedt] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    const [hinweis, setHinweis] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);

    const [freischaltenOffen, setFreischaltenOffen] = useState(false);
    const [auftragFuer, setAuftragFuer] = useState<Freelancer | null>(null);

    const laden = useCallback(async () => {
        if (!projectId) return;
        setLaedt(true);
        setFehler(null);
        try {
            // Vier Abfragen parallel statt nacheinander. Nachweise werden fuer
            // ALLE Freelancer auf einmal geholt — der Freischalt-Dialog
            // braucht sie fuer jede Zeile, und eine Abfrage je Person waere
            // ein N+1 (gleiche Regel wie in FreelancerView).
            const [stamm, nachweise, zugriff, jobs] = await Promise.all([
                fetchFreelancers(),
                fetchAllQualifications(),
                fetchProjectAccess(projectId),
                fetchAssignments({ projectId }),
            ]);
            setFreelancers(stamm);
            setQualis(nachweise);
            setZugaenge(zugriff);
            setAuftraege(jobs);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setLaedt(false);
        }
    }, [projectId]);

    useEffect(() => {
        void laden();
    }, [laden]);

    const heute = heuteIso();
    const nachId = useMemo(
        () => new Map(freelancers.map((f) => [f.id, f])),
        [freelancers],
    );

    /** Ids, die bereits aktiv freigeschaltet sind — sie fallen aus der
     *  Vorschlagsliste heraus, statt dort als „schon drin" zu verwirren. */
    const freigeschalteteIds = useMemo(
        () => new Set(zugaenge.map((z) => z.freelancerId)),
        [zugaenge],
    );

    const vorschlaege = useMemo(() => {
        const ergebnis = suggestFreelancers(
            { id: projectId, name: projectName, lat, lng },
            freelancers.filter((f) => !freigeschalteteIds.has(f.id)),
            qualis,
            heute,
        );
        return ergebnis;
    }, [projectId, projectName, lat, lng, freelancers, freigeschalteteIds, qualis, heute]);

    const auftraegeSortiert = useMemo(() => sortiereAssignments(auftraege), [auftraege]);

    // ─── Aktionen ───────────────────────────────────────────────────────────

    // Die Riegel in den Aktionen sind der zweite Zaun: die Knoepfe sind im
    // Lesemodus bereits ausgegraut, aber ein Aufruf ueber einen anderen Weg
    // darf nicht still durchlaufen — er nennt den Grund im Fehlerband.
    const freischalten = async (f: Freelancer) => {
        if (isReadOnly) {
            setHinweis(null);
            setFehler(readOnlyMessage('Es wurde niemand freigeschaltet.'));
            return;
        }
        setFehler(null);
        setHinweis(null);
        setBusy(f.id);
        try {
            await grantProjectAccess(f.id, projectId);
            setZugaenge(await fetchProjectAccess(projectId));
            setHinweis(`${f.name} ist für dieses Projekt freigeschaltet.`);
            setFreischaltenOffen(false);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setBusy(null);
        }
    };

    const entziehen = async (z: FreelancerProjectAccess) => {
        if (isReadOnly) {
            setHinweis(null);
            setFehler(readOnlyMessage('Die Freischaltung wurde nicht entzogen und es wurde kein Angebot storniert.'));
            return;
        }
        const f = nachId.get(z.freelancerId);
        const folgen = entzugsFolgen(z.freelancerId, projectId, auftraege);
        setFehler(null);
        setHinweis(null);
        setBusy(z.id);
        try {
            const offenGeblieben = await revokeProjectAccess(z.id);
            const [zugriff, jobs] = await Promise.all([
                fetchProjectAccess(projectId),
                fetchAssignments({ projectId }),
            ]);
            setZugaenge(zugriff);
            setAuftraege(jobs);
            setHinweis(
                offenGeblieben > 0
                    ? `Freischaltung entzogen (${folgen.werdenStorniert} offene Angebote storniert). ` +
                      `ACHTUNG: ${offenGeblieben} bereits angenommene Aufträge bleiben bestehen und müssen ` +
                      `mit ${f?.name ?? 'dem Freelancer'} einzeln geklärt werden.`
                    : `Freischaltung entzogen (${folgen.werdenStorniert} offene Angebote storniert).`,
            );
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setBusy(null);
        }
    };

    const stornieren = async (a: InspectionAssignment) => {
        if (isReadOnly) {
            setHinweis(null);
            setFehler(readOnlyMessage('Der Begehungsauftrag wurde nicht storniert.'));
            return;
        }
        setFehler(null);
        setHinweis(null);
        setBusy(a.id);
        try {
            const aktualisiert = await cancelAssignment(a.id);
            if (aktualisiert) {
                setAuftraege((liste) => liste.map((x) => (x.id === a.id ? aktualisiert : x)));
            }
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setBusy(null);
        }
    };

    // ─── Darstellung ────────────────────────────────────────────────────────

    if (laedt) {
        return (
            <p className="py-10 text-center text-sm text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                Freischaltungen werden geladen…
            </p>
        );
    }

    return (
        <div className="space-y-5">
            {/* Der Balken steht VOR den Listen: er ist die Erklaerung fuer die
                ausgegrauten Knoepfe darunter, nicht deren Nachtrag. */}
            {isReadOnly && (
                <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-3 text-xs font-bold flex items-start gap-2">
                    <Eye className="w-4 h-4 shrink-0 mt-px" />
                    <span>
                        {READ_ONLY_HINWEIS} Freischalten, Aufträge erteilen, Entziehen und Stornieren sind
                        hier gesperrt — bestehende Freischaltungen und Aufträge bleiben sichtbar.
                        {' '}{READ_ONLY_AUSWEG}
                    </span>
                </div>
            )}

            {fehler && (
                <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-xl p-3 text-sm flex items-start justify-between gap-3">
                    <span className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        {fehler}
                    </span>
                    <button onClick={() => setFehler(null)} className="shrink-0 hover:bg-rose-100 rounded p-1">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {hinweis && (
                <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-xl p-3 text-sm flex items-start justify-between gap-3">
                    <span className="flex items-start gap-2">
                        <Info className="w-4 h-4 mt-0.5 shrink-0" />
                        {hinweis}
                    </span>
                    <button onClick={() => setHinweis(null)} className="shrink-0 hover:bg-blue-100 rounded p-1">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* ── Freigeschaltete ── */}
            <section>
                <div className="flex items-center justify-between gap-3 mb-2">
                    <h4 className="text-xs font-bold text-slate-600 uppercase tracking-widest">
                        Freigeschaltet ({zugaenge.length})
                    </h4>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => void laden()}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                            aria-label="Neu laden"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </button>
                        <Button
                            variant="secondary"
                            onClick={() => setFreischaltenOffen(true)}
                            disabled={isReadOnly}
                            icon={<Plus className="w-4 h-4" />}
                            className="h-9 text-xs"
                            title={isReadOnly ? readOnlyTitle('Es wird niemand freigeschaltet.') : undefined}
                        >
                            Freelancer freischalten
                        </Button>
                    </div>
                </div>

                {zugaenge.length === 0 ? (
                    <p className="py-6 text-center text-sm text-slate-400 border-2 border-dashed border-slate-100 rounded-2xl">
                        Für dieses Projekt ist noch niemand freigeschaltet.
                    </p>
                ) : (
                    <ul className="space-y-2">
                        {zugaenge.map((z) => {
                            const f = nachId.get(z.freelancerId);
                            const folgen = entzugsFolgen(z.freelancerId, projectId, auftraege);
                            const arbeitet = busy === z.id;
                            return (
                                <li
                                    key={z.id}
                                    className="border border-slate-200 rounded-2xl p-3 bg-white flex flex-wrap items-start gap-3"
                                >
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-bold text-slate-800 truncate">
                                            {f?.name ?? 'Unbekannter Freelancer'}
                                        </p>
                                        <p className="text-[11px] text-slate-500 truncate">
                                            {[f?.firma, formatAnschrift(f)].filter(Boolean).join(' · ') || '—'}
                                        </p>
                                        {/* Der Warnhinweis steht AN der Zeile und nicht
                                            erst in einer Rückfrage: er ist die
                                            Entscheidungsgrundlage, nicht die Bestätigung. */}
                                        {folgen.bleibenOffen > 0 && (
                                            <p className="mt-1 text-[11px] text-amber-700 flex items-start gap-1.5">
                                                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                                                {folgen.bleibenOffen} angenommene{' '}
                                                {folgen.bleibenOffen === 1 ? 'Auftrag bleibt' : 'Aufträge bleiben'} beim
                                                Entzug bestehen und {folgen.bleibenOffen === 1 ? 'muss' : 'müssen'}{' '}
                                                einzeln geklärt werden.
                                            </p>
                                        )}
                                        {folgen.werdenStorniert > 0 && (
                                            <p className="mt-1 text-[11px] text-slate-500">
                                                {folgen.werdenStorniert} offene{' '}
                                                {folgen.werdenStorniert === 1 ? 'Angebot wird' : 'Angebote werden'} beim
                                                Entzug automatisch storniert.
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0">
                                        <Button
                                            variant="secondary"
                                            onClick={() => f && setAuftragFuer(f)}
                                            disabled={!f || arbeitet || isReadOnly}
                                            icon={<CalendarPlus className="w-4 h-4" />}
                                            className="h-9 text-xs"
                                            title={isReadOnly ? readOnlyTitle('Es wird kein Begehungsauftrag erteilt.') : undefined}
                                        >
                                            Auftrag
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={() => void entziehen(z)}
                                            disabled={arbeitet || isReadOnly}
                                            icon={
                                                arbeitet ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <UserMinus className="w-4 h-4" />
                                                )
                                            }
                                            className="h-9 text-xs"
                                            title={
                                                isReadOnly
                                                    ? readOnlyTitle('Die Freischaltung wird nicht entzogen.')
                                                    : 'Freischaltung entziehen'
                                            }
                                        >
                                            Entziehen
                                        </Button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            {/* ── Aufträge ── */}
            <section>
                <h4 className="text-xs font-bold text-slate-600 uppercase tracking-widest mb-2">
                    Begehungsaufträge ({auftraegeSortiert.length})
                </h4>
                {auftraegeSortiert.length === 0 ? (
                    <p className="py-6 text-center text-sm text-slate-400 border-2 border-dashed border-slate-100 rounded-2xl">
                        Noch kein Begehungsauftrag erteilt.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-widest text-slate-400 border-b border-slate-100">
                                    <th className="py-2 pr-3 font-bold">Freelancer</th>
                                    <th className="py-2 pr-3 font-bold">Zeitraum</th>
                                    <th className="py-2 pr-3 font-bold">Vergütung</th>
                                    <th className="py-2 pr-3 font-bold">Status</th>
                                    <th className="py-2 font-bold text-right">Aktion</th>
                                </tr>
                            </thead>
                            <tbody>
                                {auftraegeSortiert.map((a) => {
                                    const f = nachId.get(a.freelancerId);
                                    const arbeitet = busy === a.id;
                                    return (
                                        <tr key={a.id} className="border-b border-slate-50 last:border-0">
                                            <td className="py-2.5 pr-3 font-bold text-slate-700">
                                                {f?.name ?? '—'}
                                            </td>
                                            <td className="py-2.5 pr-3 text-slate-600 whitespace-nowrap">
                                                {formatZeitraum(a.zeitraumVon, a.zeitraumBis)}
                                            </td>
                                            <td className="py-2.5 pr-3 text-slate-600 tabular-nums whitespace-nowrap">
                                                {formatCents(a.verguetungCents)}
                                            </td>
                                            <td className="py-2.5 pr-3">
                                                <StatusAbzeichen status={a.status} />
                                            </td>
                                            <td className="py-2.5 text-right">
                                                {canAdminCancel(a) ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => void stornieren(a)}
                                                        disabled={arbeitet || isReadOnly}
                                                        className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:text-rose-600 disabled:opacity-50 disabled:hover:text-slate-500 disabled:cursor-not-allowed"
                                                        title={isReadOnly ? readOnlyTitle('Der Begehungsauftrag wird nicht storniert.') : undefined}
                                                    >
                                                        {arbeitet ? (
                                                            <Loader2 className="w-3 h-3 animate-spin" />
                                                        ) : (
                                                            <Ban className="w-3 h-3" />
                                                        )}
                                                        Stornieren
                                                    </button>
                                                ) : (
                                                    <span className="text-[11px] text-slate-300">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* ── Dialog: freischalten ── */}
            {freischaltenOffen && (
                <FreischaltenDialog
                    vorschlaege={vorschlaege.vorschlaege}
                    ausgeschlossen={vorschlaege.ausgeschlossen}
                    projektUnverortet={vorschlaege.projektUnverortet}
                    busyId={busy}
                    onFreischalten={freischalten}
                    onClose={() => setFreischaltenOffen(false)}
                />
            )}

            {/* ── Dialog: Auftrag ── */}
            {auftragFuer && (
                <AssignmentDialog
                    freelancer={auftragFuer}
                    projectId={projectId}
                    projectName={projectName}
                    bestehende={auftraege}
                    onCreated={(neu) => {
                        setAuftraege((liste) => [neu, ...liste]);
                        setAuftragFuer(null);
                    }}
                    onClose={() => setAuftragFuer(null)}
                />
            )}
        </div>
    );
};

// ─── Freischalt-Dialog ──────────────────────────────────────────────────────

interface DialogProps {
    vorschlaege: FreelancerMatch[];
    ausgeschlossen: FreelancerMatch[];
    projektUnverortet: boolean;
    busyId: string | null;
    onFreischalten: (f: Freelancer) => void;
    onClose: () => void;
}

const VorschlagsZeile: React.FC<{
    match: FreelancerMatch;
    busyId: string | null;
    onFreischalten: (f: Freelancer) => void;
}> = ({ match, busyId, onFreischalten }) => {
    const f = match.freelancer;
    const arbeitet = busyId === f.id;
    // Blockiergruende der Bereitschaft sperren den Knopf; ein reiner
    // Entfernungsgrund („ausserhalb des Radius") tut das NICHT — den Radius
    // darf der Innendienst bewusst uebergehen, abgelaufene Nachweise nicht
    // (dieselbe Trennung nimmt die RPC vor: sie prueft NUR die Nachweise).
    const gesperrt = !match.bereitschaft.bereit;

    return (
        <li className="border border-slate-200 rounded-2xl p-3 bg-white">
            <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-800 truncate">{f.name}</p>
                    <p className="text-[11px] text-slate-500 truncate">
                        {[f.firma, [f.zip, f.city].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || '—'}
                    </p>
                </div>
                <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-slate-700 tabular-nums">
                        {match.distanzKm === null ? '—' : formatDistanz(match.distanzKm)}
                    </p>
                    <p className="text-[11px] text-slate-400">Radius {f.einsatzradiusKm} km</p>
                </div>
            </div>

            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                {f.satzJeBegehungCents !== undefined && (
                    <span>{formatCents(f.satzJeBegehungCents)} je Begehung</span>
                )}
                {f.rating !== undefined && (
                    <span className="inline-flex items-center gap-0.5">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                        {f.rating.toFixed(1).replace('.', ',')}
                    </span>
                )}
            </div>

            {match.gruende.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-[11px] text-rose-700 list-disc list-inside">
                    {match.gruende.map((g) => (
                        <li key={g}>{g}</li>
                    ))}
                </ul>
            )}
            {match.gruende.length === 0 && match.bereitschaft.warnungen.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-[11px] text-amber-700 list-disc list-inside">
                    {match.bereitschaft.warnungen.map((w) => (
                        <li key={w}>{w}</li>
                    ))}
                </ul>
            )}

            <div className="mt-2 flex justify-end">
                <Button
                    variant={gesperrt ? 'secondary' : 'primary'}
                    onClick={() => onFreischalten(f)}
                    disabled={gesperrt || arbeitet}
                    icon={
                        arbeitet ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : gesperrt ? (
                            <Ban className="w-4 h-4" />
                        ) : (
                            <CheckCircle2 className="w-4 h-4" />
                        )
                    }
                    className="h-9 text-xs"
                    title={
                        gesperrt
                            ? 'Pflichtnachweise fehlen oder der Status ist nicht aktiv — die Freischaltung wird auch serverseitig abgelehnt.'
                            : undefined
                    }
                >
                    Freischalten
                </Button>
            </div>
        </li>
    );
};

const FreischaltenDialog: React.FC<DialogProps> = ({
    vorschlaege, ausgeschlossen, projektUnverortet, busyId, onFreischalten, onClose,
}) => (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
                <div>
                    <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                        <HardHat className="w-4 h-4 text-blue-500 shrink-0" />
                        Freelancer freischalten
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Vorschläge nach Entfernung — Pflichtnachweise werden beim Freischalten auch
                        serverseitig geprüft.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0"
                    aria-label="Schließen"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
                <section>
                    <h4 className="text-xs font-bold text-slate-600 uppercase tracking-widest mb-2">
                        Vorschläge ({vorschlaege.length})
                    </h4>
                    {vorschlaege.length === 0 ? (
                        <p className="py-6 text-center text-sm text-slate-400 border-2 border-dashed border-slate-100 rounded-2xl">
                            {projektUnverortet
                                ? 'Das Projekt ist nicht verortet — ohne Koordinaten lässt sich keine Entfernung berechnen. Standort im Projekt hinterlegen.'
                                : 'Niemand ist einsatzbereit und zugleich im Einsatzradius.'}
                        </p>
                    ) : (
                        <ul className="space-y-2">
                            {vorschlaege.map((m) => (
                                <VorschlagsZeile
                                    key={m.freelancer.id}
                                    match={m}
                                    busyId={busyId}
                                    onFreischalten={onFreischalten}
                                />
                            ))}
                        </ul>
                    )}
                </section>

                {ausgeschlossen.length > 0 && (
                    <section>
                        <h4 className="text-xs font-bold text-slate-600 uppercase tracking-widest mb-2">
                            Nicht vorgeschlagen ({ausgeschlossen.length})
                        </h4>
                        {/* Mit Begruendung statt stillschweigend weggelassen — und
                            freischaltbar, solange nur die Entfernung stoert: der
                            Einsatzradius ist eine Praeferenz, kein Verbot. */}
                        <ul className="space-y-2 opacity-90">
                            {ausgeschlossen.map((m) => (
                                <VorschlagsZeile
                                    key={m.freelancer.id}
                                    match={m}
                                    busyId={busyId}
                                    onFreischalten={onFreischalten}
                                />
                            ))}
                        </ul>
                    </section>
                )}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
                <Button variant="secondary" onClick={onClose}>
                    Schließen
                </Button>
            </div>
        </div>
    </div>
);
